import React from 'react';
import { PercentAmount } from '@akropolis-web/primitives';

import { MRT_ColumnDef, amountToAccessorValue } from 'components/MRT';
import { EtherscanLinkButton, FormattedAmount, TokenName } from 'components';
import { SystemStyleObject, colors, styled } from 'core/styles';
import { isCriticalPriceDeviation, makeContractUrl } from 'domain/utils';
import { combine, left } from 'utils/either';

import { TableData } from '.';

const headCellAlignStyles: SystemStyleObject = {
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'flex-end',
  },
};

const numberFilters = ['greaterThan', 'lessThan', 'between'];

export const priceChartsColumns: MRT_ColumnDef<TableData>[] = [
  {
    header: 'Asset',
    accessorFn: originalRow => originalRow.token.symbol,
    id: 'asset',
    columnFilterModeOptions: ['fuzzy', 'contains', 'startsWith', 'endsWith'],
    size: 145,
    enableColumnActions: false,
    Cell: ({ row, renderedCellValue }) => (
      <AssetCell>
        <TokenName token={row.original.token} symbol={renderedCellValue} />
        <EtherscanLinkButton
          href={makeContractUrl(row.original.token.address, row.original.token.network)}
          network={row.original.token.network}
        />
      </AssetCell>
    ),
  },
  {
    header: 'Market Price',
    accessorFn: originalRow => amountToAccessorValue(originalRow.marketPrice),
    id: 'marketPrice',
    enableColumnActions: false,
    columnFilterModeOptions: numberFilters,
    filterFn: numberFilters[0],
    sortingFn: 'basic',
    size: 110,
    Cell: ({ row }) => <FormattedAmount sum={row.original.marketPrice} />,
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Oracle Price',
    accessorFn: originalRow => amountToAccessorValue(originalRow.oraclePrice),
    id: 'oraclePrice',
    enableColumnActions: false,
    columnFilterModeOptions: numberFilters,
    filterFn: numberFilters[0],
    sortingFn: 'basic',
    size: 110,
    Cell: ({ row }) => {
      return (
        <>
          {row.original.priceDeviation.fold(
            priceDeviation => (
              <DifferenceBox isCritical={isCriticalPriceDeviation(priceDeviation)}>
                <FormattedAmount sum={row.original.oraclePrice} />
              </DifferenceBox>
            ),
            () => (
              <FormattedAmount sum={row.original.oraclePrice} />
            ),
          )}
        </>
      );
    },
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Difference',
    accessorFn: originalRow => amountToAccessorValue(originalRow.difference),
    id: 'difference',
    enableColumnActions: false,
    columnFilterModeOptions: numberFilters,
    filterFn: numberFilters[0],
    sortingFn: 'basic',
    size: 110,
    Cell: ({ row }) => {
      return combine(row.original.priceDeviation, row.original.difference).fold(
        ([priceDeviation, difference]) => (
          <DifferenceBox isCritical={isCriticalPriceDeviation(priceDeviation)}>
            <FormattedAmount sum={difference} />

            {isCriticalPriceDeviation(priceDeviation) && (
              <div>
                (
                <FormattedAmount
                  sum={new PercentAmount(priceDeviation.abs())}
                  symbolSize="inherit"
                />
                )
              </div>
            )}
          </DifferenceBox>
        ),
        error => <FormattedAmount sum={left(error)} />,
      );
    },
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
];

const AssetCell = styled('div')(() => ({
  height: 'calc(1em * 1.43)',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}));

const DifferenceBox = styled('div', { shouldForwardProp: prop => prop !== 'isCritical' })<{
  isCritical: boolean;
}>(({ isCritical }) => ({
  color: isCritical ? colors.bittersweet : colors.black,
}));
